import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDeleteRole } from '../hooks/api/use-roles.hook';
import { RoleApiModel } from '../../../types/models/role.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchRoles } from '../api/roles.api';

export const Roles = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteRole, { loading: deletingRole, error: deleteError }] = useDeleteRole();

  const columns: ColDef<RoleApiModel>[] = [
    { field: 'name', filter: true, sortable: true, filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } },
    {
      field: 'permissions',
      valueGetter: (params) => params.data?.permissions?.join(', '),
    },
    {
      headerName: 'Actions',
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<RoleApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteRole({ roleId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Role',
                message: `Are you sure you want to delete role "${node.data?.name}"?`,
                error: deleteError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getRoles: Parameters<typeof InfniteScrollingGrid<RoleApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchRoles({ name: filterQuery?.name, cursor, limit, sort: sortQuery });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Roles'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Roles</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Role
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingRole} columnDefs={columns} onGetRows={getRoles} />
        </Box>
      </Box>
    </Box>
  );
};
